<template>
  <JsonLDModel :jsonOptions="jsonOptions" />    
  <NuxtLayout>
    <div class="slot-body">
      <h1 v-if="dadosSSR?.nameModel" class="ssr-model-title">
        {{ (route.params.language === 'pt-br' ? 'Créditos do Ensaio de ' : 'Photoshoot Credits of ') + dadosSSR.nameModel }}
      </h1>
      <NavBarHeader
        v-if="dataModel"
        :model-info="dadosSSR"
        :model-data="dataModel"
      >
        <ModelCredits :model="dadosSSR" :credits="dataModel" />
        <div class="block block--gray">
          <ClientOnly>
            <BannerAssineHorizontal :path="bannerAssineHorizontal" />
          </ClientOnly>
        </div>
      </NavBarHeader>
      <ModalSubscribePlans
        @showModalSubscribePlans="showModalSubscribePlans"
        :showSubscribe="showSubscribe"
        :plans="storePlan.plans"
        v-if="storePlan.plans"
      />
    </div>
  </NuxtLayout>
</template>
<script lang="ts" setup>
// SEO: página de conteúdo explícito — marcação que o Google pede para não
// classificar o site inteiro como adulto. Ver composables/useAdultRating.ts
useAdultRating();
import ModelCredits from "@/components/Model/ModelCredits.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import ModalSubscribePlans from "@/components/Modais/ModalSubscribePlansAndCredits.vue";
import NavBarHeader from "@/components/Model/NavBarHeader.vue";
import api from "@/api";
import { isMobile } from "@/shared/utils/mobile";
import JsonLDModel from "@/components/JsonLDModel.vue";
import { publicCreditsPayload } from "@/utils/publicModelPayload";

definePageMeta({
  middleware: ["not-found-lang"],
});

const route = useRoute();
const storeUser = userStore();
const storePlan = planStore();

const ensaio = useEnsaioRota();
const urlCreditos = `v2/modelos/${ensaio.nome}/perfilWallpaperCredits?${ensaio.query}`;

const [metadados, creditosSSR] = await Promise.all([
  useEnsaioMetadados(),
  useEnsaioPublico(
    "model-creditos-public",
    `v2/modelos/${ensaio.nome}/perfilWallpaperCredits`,
    publicCreditsPayload
  ),
]);
exigirMetadados(metadados.error.value, metadados.data.value);

const dadosSSR = ref(metadados.data.value);

const canonical = useModelCanonical(route.params.name, route.params.language);
const seo = await useSeoConfig(String(route.name), String(route.params.language));
useHead({
  templateParams: {
    subTitle: null,
    subPage: (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.title || (route.params.language === 'pt-br' ? "Créditos do Ensaio, Equipe e Produção" : "Credits for the Photoshoot, Team and Production")),
    page_url: canonical,
    page_description:
      (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.description || (route.params.language === 'pt-br' ? "Conheça a equipe por trás do ensaio no Bella da Semana. Fotógrafos, produtores e todos os créditos. Detalhes da produção exclusiva." : "Meet the team behind the photoshoot for Bella Club. Photographers, producers, and all credits. Details of the exclusive production.")),
    page_image: shareImage(seo.value?.image, dadosSSR.value.imgPerfil),
    page_keywords:
      seo.value?.keywords ||
      (dadosSSR.value.meta_keywords ??
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog"),
    page_locale: route.params.language as string,
    page_url_pt:
      "https://belladasemana.com.br/pt-br/modelos/" +
      route.params.name +
      "/photo",
    page_url_en:
      "https://belladasemana.com.br/en/modelos/" + route.params.name + "/photo",
    page_canonical: canonical,
  },
});

const jsonOptions = ref({
  type: "Person",
  name: dadosSSR.value.nameModel || dadosSSR.value.seoTitleModel || "",
  jobTitle: 'Model',
  image: dadosSSR.value.imgPerfil ?? "",
  url: canonical
});

const bannerAssineHorizontal = ref("");
const showSubscribe = ref(false);
// Créditos e flags das abas. O conteúdo não depende de quem vê: o cliente só
// busca quando o SSR não trouxe (navegação entre abas, falha da API).
const creditosCliente = ref<any>(null);
const dataModel = computed(() => creditosCliente.value ?? creditosSSR.data.value);

onMounted(() => {
  getBannerPath();
  if (!creditosSSR.data.value) carregarCreditos();
});

async function carregarCreditos() {
  const dados = await buscarEnsaioNoCliente(urlCreditos);
  const creditos = publicCreditsPayload(dados);
  if (creditos) creditosCliente.value = creditos;
}

function getBannerPath() {
  if (!sessionStorage.getItem("bannerAssineHorizontal"))
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}
function showModalSubscribePlans(value: boolean) {
  if (!storeUser.isActiveUser) {
    showSubscribe.value = value;
    if (value == true)
      showModalPlans(
        "Clicar para reproduzir o video sem ter uma assinatura ativa"
      );
  }
}
function showModalPlans(acionador: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "paywall",
    paywall: { acionador: acionador },
  });
}
</script>
<style scoped lang="css">
.tab-panels :deep(.tab-panel) {
  width: 98%;
}
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.title {
  font-family: "Bebas-Neue-Light";
  font-size: 60px;
  font-weight: 500;
  color: black;
}

.title-models {
  font-family: "Exo-2";
  font-size: 32px;
  font-weight: 600;
  color: #b20000;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
}

.ssr-model-title {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
}
</style>
